<template>
    <EditComponent
        ref="refEditComponent"
        dialog-id="editSelectionModeExperienceDrawer"
        open-dialog-event="experiences:selection-mode"
        :open-dialog-post-function="handleDialogOpen"
        data-testid="profile-edition-experience-select-mode-drawer"
        confirm-data-testid="edit-manual-experience-confirm"
        :confirm-enabled="false"
        :is-visible-cta-cancel="false"
        :is-visible-cta-confirm="false"
        :track-section-opened="EditionOpenedSection.EXPERIENCES"
    >
        <template #edit-header>{{ t('profile.experience.import.selection.title') }}</template>
        <template #edit-body>
            <div class="resume-importer__selection">
                <div data-testid="profile-edition-experience-select-mode-resume-import" @click="selectResumeImport">
                    <div class="resume-importer__selection-section">
                        <VJoyIcon name="cloud-upload" size="large" />
                        <div class="resume-importer__selection-section-method">
                            <div class="resume-importer__selection-section-method-title">
                                {{ t('profile.experience.import.selection.section.any_resume.title') }}
                            </div>
                            <div class="resume-importer__selection-section-method-subtitle">
                                {{ t('profile.experience.import.selection.section.any_resume.subtitle') }}
                            </div>
                        </div>
                        <VJoyIcon name="chevron-right" size="xsmall" />
                    </div>
                </div>
                <div data-testid="profile-experience-select-mode-manual-addition" class="resume-importer__selection-section" @click="selectManual">
                    <VJoyIcon name="freelancer-placeholder" size="large" />
                    <div class="resume-importer__selection-section-method">
                        <div class="resume-importer__selection-section-method-title">
                            {{ t('profile.experience.import.selection.section.manual.title') }}
                        </div>
                        <div class="resume-importer__selection-section-method-subtitle">
                            {{ t('profile.experience.import.selection.section.manual.subtitle') }}
                        </div>
                    </div>
                    <VJoyIcon name="chevron-right" size="xsmall" />
                </div>
            </div>
        </template>
    </EditComponent>
</template>

<script setup lang="ts">
    import {ref} from 'vue';
    import {storeToRefs} from 'pinia';
    import {onMounted, useRouter, useTranslation, navigateTo} from '#imports';
    import {VJoyIcon} from '@maltjoy/core-vue';
    import EditComponent from '~/components/edit/EditComponent.vue';
    import {eventBus} from '~/config/events';
    import {EditionOpenedEntryPoint, EditionOpenedSection} from '~/config/TrackingEvents';
    import {useProfileStore} from '~/store/profileStore';

    const {t} = useTranslation();
    const router = useRouter();
    const {profile} = storeToRefs(useProfileStore());

    const refEditComponent = ref<typeof EditComponent>();
    const entryPoint = ref<EditionOpenedEntryPoint>(EditionOpenedEntryPoint.PROFILE_PAGE);

    function handleDialogOpen(payload?: {entryPoint?: EditionOpenedEntryPoint}): void {
        entryPoint.value = payload?.entryPoint ?? EditionOpenedEntryPoint.PROFILE_PAGE;
    }

    onMounted(() => {
        const route = router.currentRoute.value;
        if (Object.prototype.hasOwnProperty.call(route.query, 'editExperiences') && profile.value?.editMode) {
            eventBus.emit('experiences:selection-mode', {entryPoint: route.query.entryPoint as EditionOpenedEntryPoint});
            const queryParams = {...route.query};
            delete queryParams.editExperiences;
            if (
                queryParams.entryPoint === EditionOpenedEntryPoint.MESSENGER ||
                queryParams.entryPoint === EditionOpenedEntryPoint.SOURCING_PROJECT_APPLICATION
            ) {
                entryPoint.value = queryParams.entryPoint as EditionOpenedEntryPoint;
                delete queryParams.entryPoint;
            }
            router.replace({query: queryParams});
        }
    });

    function selectResumeImport(): void {
        refEditComponent.value?.close();
        eventBus.emit('experiences:resume-import-mode', {entryPoint: entryPoint.value});
        resetEntryPoint();
    }

    async function selectManual(): Promise<void> {
        await navigateTo({path: `/profile/experiences/new`, query: {entryPoint: entryPoint.value}});
    }

    function resetEntryPoint(): void {
        entryPoint.value = EditionOpenedEntryPoint.PROFILE_PAGE;
    }
</script>

<style lang="scss" scoped>
    .resume-importer__selection {
        display: flex;
        flex-direction: column;

        &-section {
            display: flex;
            flex-direction: row;
            align-items: center;
            border: 1px solid var(--joy-color-neutral-30);
            border-radius: var(--joy-core-radius-2);
            margin-bottom: 20px;
            cursor: pointer;

            box-shadow: var(--joy-core-elevation-1);
            transition: border-color var(--joy-transition-duration-default) var(--joy-transition-timing-function);

            &:hover {
                border-color: var(--joy-color-border-hover);
            }

            &-method {
                flex: 2;

                &-title {
                    font-size: var(--joy-font-size-primary-600);
                    font-weight: var(--joy-font-weight-bold);
                }
            }

            .joy-icon {
                margin: var(--joy-core-spacing-5);
            }
        }
    }
</style>
